import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ContentUploadDialog } from "@/components/content/ContentUploadDialog";
import { PasteContentDialog } from "@/components/dashboard/PasteContentDialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import coursesIntroVideo from "@/assets/shattaraai_courses_intro.mp4";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

export const DashboardHeroMastery: React.FC = () => {
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [pasteDialogOpen, setPasteDialogOpen] = useState(false);
  const [learnMoreOpen, setLearnMoreOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleCourseRequestCreated = (requestId: string) => {
    navigate(`/course/creating/${requestId}`);
  };

  const handleCreateFromPrompt = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim() || isSubmitting || !user) return;

    setIsSubmitting(true);
    try {
      const { data, error } = await supabase
        .from("course_generation_requests")
        .insert({
          user_id: user.id,
          source: "prompt",
          prompt: prompt.trim(),
        })
        .select()
        .single();

      if (error) throw error;

      // Fire-and-forget edge function invocation
      supabase.functions
        .invoke("generate-course", { body: { requestId: data.id } })
        .catch((err) => console.error("Course generation invoke failed:", err));

      navigate(`/course/creating/${data.id}`);
    } catch (err: any) {
      console.error("Failed to create course request:", err);
      toast({
        title: "Error",
        description: err?.message || "Failed to start course creation",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex w-full flex-col items-center">
      <div className="mb-5 flex flex-col items-center gap-2">
        <h2 className="text-xl text-center sm:text-3xl font-medium xl:text-4xl">
          What do you want to master?
        </h2>
        <p className="text-base text-muted-foreground text-center">
          Create a personalized course by chatting with AI.{" "}
          <Popover open={learnMoreOpen} onOpenChange={setLearnMoreOpen}>
            <PopoverTrigger asChild>
              <button className="inline-flex cursor-pointer items-center gap-1 underline underline-offset-4 text-muted-foreground transition-colors hover:text-foreground">
                Learn more
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-arrow-up-right h-3.5 w-3.5"
                  aria-hidden="true"
                >
                  <path d="M7 7h10v10" />
                  <path d="M7 17 17 7" />
                </svg>
              </button>
            </PopoverTrigger>
            <PopoverContent align="center" sideOffset={8} className="w-[calc(100vw-2rem)] sm:w-[440px] rounded-2xl p-5 leading-relaxed">
              <div className="mb-1 flex items-start justify-between">
                <h4 className="flex items-center gap-2 font-medium whitespace-nowrap">
                  <span className="h-2 w-2 animate-pulse rounded-full bg-correct" />
                  Test your knowledge on everything
                </h4>
              </div>
              <p className="mb-4 text-sm text-muted-foreground">
                Build custom courses, get lesson, concept, and question breakdowns, track your progress, and review weak spots.
              </p>
              <div className="mb-3">
                <video
                  src={coursesIntroVideo}
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="auto"
                  className="h-auto w-full rounded-xl border border-border object-cover"
                />
              </div>
              <div className="flex justify-end">
                <button
                  onClick={() => setLearnMoreOpen(false)}
                  className="inline-flex items-center justify-center whitespace-nowrap font-medium ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-9 rounded-lg px-3 text-sm"
                >
                  Got it!
                </button>
              </div>
            </PopoverContent>
          </Popover>
        </p>
      </div>

      <div className="w-full md:max-w-[512px] xl:max-w-[672px]">
        <form
          onSubmit={handleCreateFromPrompt}
          className="relative flex w-full flex-col justify-center rounded-3xl border border-border bg-card py-[5px] pl-3.5 pr-2 shadow-sm transition-all duration-150 space-y-1 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2"
        >
          <div className="flex flex-col space-y-2" />
          <div className="relative flex w-full flex-col transition-all duration-500 ease-in">
            <div className="relative flex w-full items-end">
              <textarea
                className="mx-1 my-2.5 max-h-96 w-full resize-none border-0 bg-transparent p-0 text-base leading-6 outline-none placeholder:text-muted-foreground"
                placeholder="Create a course on blockchain technology"
                rows={3}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                disabled={isSubmitting}
              />
              <div className="absolute right-0 flex items-center">
                <button
                  type="submit"
                  disabled={!prompt.trim() || isSubmitting}
                  className="inline-flex h-[34px] w-[34px] items-center justify-center rounded-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60 transition-opacity"
                >
                  {isSubmitting ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="lucide lucide-arrow-up h-5 w-5"
                      aria-hidden="true"
                    >
                      <path d="m5 12 7-7 7 7" />
                      <path d="M12 19V5" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>

      <div className="mt-3 flex flex-row items-center justify-center gap-2">
        <p className="text-sm text-muted-foreground">or import from</p>
        <div className="flex w-auto flex-col text-center">
          <div className="flex flex-row items-center justify-center gap-2">
            <div className="w-auto">
              <div
                className="group relative cursor-pointer rounded-full border border-border bg-card text-card-foreground shadow-sm transition-all duration-200 hover:shadow-md"
                onClick={() => setUploadDialogOpen(true)}
              >
                <div className="flex flex-row items-center justify-center gap-2 px-2.5 py-1.5">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-upload h-4 w-4 flex-shrink-0 text-muted-foreground transition-colors group-hover:text-foreground"
                    aria-hidden="true"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" x2="12" y1="3" y2="15" />
                  </svg>
                  <h3 className="text-sm font-medium text-muted-foreground transition-colors group-hover:text-foreground">
                    Upload
                  </h3>
                </div>
              </div>
            </div>

            <div className="w-auto">
              <div
                className="group relative cursor-pointer rounded-full border border-border bg-card text-card-foreground shadow-sm transition-all duration-200 hover:shadow-md"
                onClick={() => setPasteDialogOpen(true)}
              >
                <div className="flex flex-row items-center justify-center gap-2 px-2.5 py-1.5">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-link2 h-4 w-4 flex-shrink-0 text-muted-foreground transition-colors group-hover:text-foreground"
                    aria-hidden="true"
                  >
                    <path d="M9 17H7A5 5 0 0 1 7 7h2" />
                    <path d="M15 7h2a5 5 0 1 1 0 10h-2" />
                    <line x1="8" x2="16" y1="12" y2="12" />
                  </svg>
                  <h3 className="text-sm font-medium text-muted-foreground transition-colors group-hover:text-foreground">
                    Paste
                  </h3>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ContentUploadDialog
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
        mode="course"
        onCourseRequestCreated={handleCourseRequestCreated}
      />
      <PasteContentDialog
        open={pasteDialogOpen}
        onOpenChange={setPasteDialogOpen}
        mode="course"
        onCourseRequestCreated={handleCourseRequestCreated}
      />
    </div>
  );
};
